import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Box, Popover, Typography } from '@mui/material';
import { BRAND_COLORS } from '../../../../styles/theme';
import { TrendChartPoint } from '../types/health.types';
import { explainDataPoint } from '../config/formulaExplanations';
import { useTranslation } from '../../../../i18n';

type DataPoint = TrendChartPoint;

interface TrendChartProps {
  unit: string;
  dataPoints: DataPoint[];
  trendValue?: number | null;
  /** Backend parameter name — enables the click-a-point formula popup. */
  parameter?: string;
}

interface PointPopup {
  point: DataPoint;
  fullDate: string;
  position: { top: number; left: number };
}

export const TrendChart: React.FC<TrendChartProps> = ({
  unit,
  dataPoints,
  trendValue,
  parameter,
}) => {
  const { t } = useTranslation();
  const [popup, setPopup] = React.useState<PointPopup | null>(null);
  if (dataPoints.length < 2) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Insufficient data for trend visualization (need at least 2 points)
        </Typography>
      </Box>
    );
  }

  // Prepare chart data with month indicators
  const chartData = dataPoints.map((point, index) => {
    const dateObj = new Date(point.date);
    return {
      index,
      date: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      month: dateObj.toLocaleDateString('en-US', { month: 'short' }),
      value: point.value,
      upperBound: point.stdDev ? point.value + point.stdDev : undefined,
      lowerBound: point.stdDev ? point.value - point.stdDev : undefined,
      // Pass through metadata for tooltip display
      flight_hours: point.flight_hours,
      shared_by: point.shared_by,
      avg_cht_1: point.avg_cht_1,
      avg_oat: point.avg_oat,
      'avg_gps_alt[m]': point['avg_gps_alt[m]'],
      avg_system_efficiency: point.avg_system_efficiency,
      avg_mat: point.avg_mat,
      avg_engine_power: point.avg_engine_power,
      rawPoint: point, // Keep full point for dynamic access
    };
  });

  // Create month markers for X-axis
  const monthMarkers: Array<{ position: number; label: string }> = [];
  const seenMonths = new Set<string>();

  chartData.forEach((point, index) => {
    if (!seenMonths.has(point.month)) {
      seenMonths.add(point.month);
      monthMarkers.push({
        position: index,
        label: point.month,
      });
    }
  });

  // Calculate adaptive Y-axis domain
  const allValues = chartData.map((d) => d.value).filter((v) => v !== undefined);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = maxValue - minValue;
  const padding = Math.max(range * 0.1, 1); // 10% padding or minimum 1 unit
  const yAxisMin = Math.max(0, minValue - padding); // Don't go below 0 for percentages
  const yAxisMax = maxValue + padding;

  // Calculate trend line data for visualization
  const trendLineData = chartData
    .map((_, index) => {
      if (trendValue === null || trendValue === undefined) return null;

      // Simple linear trend calculation
      const firstValue = chartData[0].value;
      const trendLineValue = firstValue + trendValue * index;
      return {
        index,
        trendValue: trendLineValue,
      };
    })
    .filter(Boolean);

  // Get trend color - simple blue for trend line
  const getTrendColor = () => {
    return BRAND_COLORS.forestGreen?.main || '#2B5540';
  };

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ name: string; value?: number; color: string; payload?: { index?: number } }>;
    label?: string;
  }) => {
    if (!active || !payload || !payload.length) return null;

    // Get the payload data which has the index
    const dataIndex = payload[0]?.payload?.index;
    const dataPoint = dataIndex !== undefined ? chartData[dataIndex] : undefined;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 1,
          fontSize: '0.75rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          {dataPoint?.fullDate || label}
        </Typography>
        {payload.map((entry, index: number) => (
          <div key={index} style={{ color: entry.color }}>
            {entry.name === 'value' && `${entry.value?.toFixed(2)} ${unit}`}
            {entry.name === 'trendValue' && `Trend: ${entry.value?.toFixed(2)} ${unit}`}
            {entry.name === 'upperBound' &&
              entry.value &&
              `Upper bound: ${entry.value?.toFixed(2)} ${unit}`}
            {entry.name === 'lowerBound' &&
              entry.value &&
              `Lower bound: ${entry.value?.toFixed(2)} ${unit}`}
          </div>
        ))}

        {/* A point from a flight another organization shared names its owner (#1693) */}
        {dataPoint?.shared_by && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
            {t('dronePageShared.trendPointShared', { org: dataPoint.shared_by })}
          </Typography>
        )}

        {/* Additional metadata for calculated parameters */}
        {dataPoint?.flight_hours && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            Flight Hours: {dataPoint.flight_hours?.toFixed(2)} h
          </Typography>
        )}
        {dataPoint?.avg_cht_1 !== undefined && dataPoint?.avg_cht_1 !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            CHT1: {dataPoint.avg_cht_1?.toFixed(2)} °C
          </Typography>
        )}
        {dataPoint?.avg_oat !== undefined && dataPoint?.avg_oat !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            OAT: {dataPoint.avg_oat?.toFixed(2)} °C
          </Typography>
        )}
        {dataPoint?.['avg_gps_alt[m]'] !== undefined && dataPoint?.['avg_gps_alt[m]'] !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            GPS Alt: {dataPoint['avg_gps_alt[m]']?.toFixed(2)} m
          </Typography>
        )}
        {dataPoint?.avg_system_efficiency !== undefined &&
          dataPoint?.avg_system_efficiency !== null && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              System Eff: {dataPoint.avg_system_efficiency?.toFixed(2)} %
            </Typography>
          )}
        {dataPoint?.avg_mat !== undefined && dataPoint?.avg_mat !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            MAT: {dataPoint.avg_mat?.toFixed(2)} °C (
            {((dataPoint.avg_mat * 9) / 5 + 32)?.toFixed(2)} °F)
          </Typography>
        )}
        {dataPoint?.avg_engine_power !== undefined && dataPoint?.avg_engine_power !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Engine Power: {(dataPoint.avg_engine_power / 1000)?.toFixed(2)} kW
          </Typography>
        )}
      </Box>
    );
  };

  // Click a data point -> small popup with the formula, the reasoning, and
  // this flight's values substituted into the equation.
  const handleChartClick = (
    state: { activeTooltipIndex?: number } | null,
    event: React.MouseEvent
  ) => {
    const index = state?.activeTooltipIndex;
    if (index === undefined || index === null) return;
    const row = chartData[index];
    if (!row) return;
    setPopup({
      point: row.rawPoint,
      fullDate: row.fullDate,
      position: { top: event.clientY, left: event.clientX },
    });
  };

  const explanation = popup ? explainDataPoint(parameter, popup.point) : null;

  return (
    <Box sx={{ width: '100%', height: 200, mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 30, bottom: 60 }}
          onClick={handleChartClick}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="index"
            fontSize={11}
            tick={{ fontSize: 11 }}
            tickLine={true}
            axisLine={true}
            ticks={monthMarkers.map((m) => m.position)}
            tickFormatter={(index: number) => {
              const marker = monthMarkers.find((m) => m.position === index);
              return marker ? marker.label : '';
            }}
          />
          <YAxis
            fontSize={10}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => `${value.toFixed(0)}`}
            domain={[yAxisMin, yAxisMax]}
            tickCount={6}
            type="number"
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Confidence bands using std dev */}
          {dataPoints.some((p) => p.stdDev) && (
            <>
              <Line
                type="monotone"
                dataKey="upperBound"
                stroke="#ddd"
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="lowerBound"
                stroke="#ddd"
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
              />
            </>
          )}

          {/* Actual data line */}
          <Line
            type="monotone"
            dataKey="value"
            stroke={BRAND_COLORS.forestGreen?.main || '#2B5540'}
            strokeWidth={2}
            dot={{ fill: BRAND_COLORS.forestGreen?.main || '#2B5540', strokeWidth: 2, r: 3 }}
            activeDot={{ r: 5, fill: BRAND_COLORS.forestGreen?.main || '#2B5540' }}
          />

          {/* Trend line */}
          {trendValue !== null && trendValue !== undefined && trendLineData.length > 0 && (
            <Line
              type="monotone"
              dataKey="trendValue"
              stroke={getTrendColor()}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              data={trendLineData}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Click-a-point formula popup */}
      <Popover
        open={popup !== null}
        onClose={() => setPopup(null)}
        anchorReference="anchorPosition"
        anchorPosition={popup?.position}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {popup && (
          <Box sx={{ p: 1.5, maxWidth: 340, fontSize: '0.8rem' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {popup.fullDate} — {popup.point.value.toFixed(2)} {unit}
            </Typography>

            {explanation ? (
              <>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {explanation.reasoning}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'text.secondary' }}
                >
                  {t('healthPanel.pointFormula')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  {explanation.formula}
                </Typography>

                {explanation.substitution.length > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      {t('healthPanel.pointValues')}
                    </Typography>
                    {explanation.substitution.map((line, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        sx={{ display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </>
                )}

                {!explanation.direct && explanation.substitution.length === 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    {t('healthPanel.pointNoSources')}
                  </Typography>
                )}
              </>
            ) : (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
              >
                {t('healthPanel.pointNoFormula')}
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};
